/**
 * The items over the Warehouse page's list (SCRUM-647). Owner, 19.10.2026: «вообще убирай этот рентал desk из меню оставь варехауз как было и
 * всё это на странице варехауза размести» — a row of items in the shows-tabs look.
 *
 * SCRUM-923, owner 29.10.2026: «Давай оставим пока Warehouse. И под ним будет всё жить что касается прокатчика», «Плюс надо добавить пункт
 * crew», and ten minutes later «давай варехауз первым пунктом. пока что»: the page opens on Warehouse.
 *
 * RESTRUCTURED 30.10.2026 by the owner («у каждой вещи один хозяин»; «макет одобряю»): Dashboard · Warehouse · Projects · Crew.
 *  - «Today» became the Dashboard: a summary only, every block a short list with a link to the item that owns it.
 *  - «To answer» is gone («to answer можно уничтожить»): an offer request is a project at the stage «To answer» INSIDE Projects, which has
 *    the filter To answer · Agreed · Past. The old `?tab=answer` and `?tab=today` addresses land on Projects / Dashboard.
 *  - A project's crew lives only in Crew; the Projects card has one line that opens Crew on that show (`show=`).
 * The page still opens on Warehouse (what opened first before). The Projects item keeps the id `shows` — its address was `?tab=shows`.
 * The address follows the item; every old address opens what it opened. Pure, so it is tested without a page.
 */
export const WAREHOUSE_TABS = ["dashboard", "warehouse", "shows", "crew"] as const;
export type WarehouseTab = (typeof WAREHOUSE_TABS)[number];

/** The item the page opens on: the Warehouse list (it opened first before the restructuring, and the owner kept it). */
export const DEFAULT_WAREHOUSE_TAB: WarehouseTab = "warehouse";

/** The three stages of a project (owner, 30.10.2026): a request waiting for my answer, an agreed one still to come, one that has passed. */
export const PROJECT_STAGES = ["answer", "agreed", "past"] as const;
export type ProjectStage = (typeof PROJECT_STAGES)[number];

/** Where inside an item an address points: Projects' stage and card, Crew's show. Nothing here is a tab of its own. */
export interface TabTarget {
  stage?: ProjectStage;
  /** A show: Projects brings its card (or its request row) into view, Crew brings its crew card. */
  show?: string;
}

/** The old names of the removed items, and where they land. */
const OLD_ITEMS: Record<string, { tab: WarehouseTab; stage?: ProjectStage }> = {
  today: { tab: "dashboard" },
  answer: { tab: "shows", stage: "answer" },
};

/** The item an address names; anything else is the Warehouse itself. */
export function parseWarehouseTab(value: string | null): WarehouseTab {
  if (value === null || value === "warehouse") return DEFAULT_WAREHOUSE_TAB;
  if (value in OLD_ITEMS) return OLD_ITEMS[value]!.tab;
  return (WAREHOUSE_TABS as readonly string[]).includes(value) ? (value as WarehouseTab) : DEFAULT_WAREHOUSE_TAB;
}

export function parseProjectStage(value: string | null | undefined): ProjectStage | undefined {
  return (PROJECT_STAGES as readonly string[]).includes(value ?? "") ? (value as ProjectStage) : undefined;
}

/**
 * Everything an address says about the item: the item, and — for Projects — the stage; for Projects and Crew — the show. The old
 * `?tab=answer` is Projects on the To answer stage, whatever else the address holds.
 */
export function parseWarehouseAddress(search: URLSearchParams): { tab: WarehouseTab } & TabTarget {
  const raw = search.get("tab");
  const tab = parseWarehouseTab(raw);
  const old = raw !== null ? OLD_ITEMS[raw] : undefined;
  const show = search.get("show");
  const stage = tab === "shows" ? (old?.stage ?? parseProjectStage(search.get("stage"))) : undefined;
  return { tab, ...(stage ? { stage } : {}), ...((tab === "shows" || tab === "crew") && show ? { show } : {}) };
}

/**
 * The address an item shows. `back` is the way back to an offer (SCRUM-636): it rides along on the Warehouse item only. Projects shows its
 * stage unless it is Agreed (the stage it opens on); Projects and Crew show the show they were pointed at.
 */
export function warehouseTabAddress(tab: WarehouseTab, back: string | null, target: TabTarget = {}): string {
  if (tab === "warehouse") return back ? `/warehouse?back=${encodeURIComponent(back)}` : "/warehouse";
  const parts: string[] = [`tab=${tab}`];
  if (tab === "shows" && target.stage && target.stage !== "agreed") parts.push(`stage=${target.stage}`);
  if ((tab === "shows" || tab === "crew") && target.show) parts.push(`show=${encodeURIComponent(target.show)}`);
  return `/warehouse?${parts.join("&")}`;
}
